import { useMemo } from 'react';
import type { NewSizingSnapshot } from '@/sizing/newSizingEngine';
import type { SizingNarrativeData, PersonaTiming } from '@/types/narrative';

type UseNewSizingNarrativeArgs = {
  snapshot: NewSizingSnapshot | null;
  modelName?: string;
  serverSku?: string;
  serverDisplayName?: string;
  gpusPerServer?: number;
  hbmBytesPerGpu?: number;
  weightsGiB?: number;
  weightsGiBUnshared?: number;
  runtimeOverheadGiB?: number;
  usableKvGiBPerGpu?: number;
  bridgeGroupSize?: number;
  conversationTurns?: number;
  latencyMs?: number;
  batchSize?: number;
  personas?: Array<{
    id: string;
    name: string;
    sharePercent: number;
    population: number;
    inputTokensPerConversation: number;
    outputTokensPerConversation: number;
    readSeconds?: number;
    thinkSeconds?: number;
    ttftMs?: number;
    decodeTps?: number;
  }>;
};

export function useNewSizingNarrative(args: UseNewSizingNarrativeArgs): SizingNarrativeData | null {
  const {
    snapshot,
    modelName = 'Unknown Model',
    serverSku,
    serverDisplayName,
    gpusPerServer = 8,
    hbmBytesPerGpu,
    weightsGiB = 0,
    weightsGiBUnshared,
    runtimeOverheadGiB,
    usableKvGiBPerGpu,
    bridgeGroupSize = 1,
    conversationTurns = 10,
    latencyMs = 3000,
    batchSize = 1,
    personas = [],
  } = args;

  return useMemo(() => {
    if (!snapshot) return null;

    // Transform personas
    const personaSummaries = personas.map(p => {
      const totalTokens = p.inputTokensPerConversation + p.outputTokensPerConversation;
      const kvPerSession = (totalTokens * snapshot.kvBytesPerToken) / (1024 ** 3); // Convert to GiB
      
      return {
        id: p.id,
        label: p.name,
        population: p.population,
        mcActiveP95: Math.round(p.population * (snapshot.p95Active / Math.max(1, personas.reduce((sum, pp) => sum + pp.population, 0)))),
        inputTokensPerConversation: p.inputTokensPerConversation,
        outputTokensPerConversation: p.outputTokensPerConversation,
        tokensPerConversation: totalTokens,
        kvGiBPerSession: kvPerSession,
        sharePct: p.sharePercent,
      };
    });

    // Calculate aggregate token metrics
    const totalShare = personas.reduce((sum, p) => sum + p.sharePercent, 0);
    const aggregateInputTokens = personas.reduce(
      (sum, p) => sum + (p.inputTokensPerConversation * p.sharePercent / 100),
      0
    );
    const aggregateOutputTokens = personas.reduce(
      (sum, p) => sum + (p.outputTokensPerConversation * p.sharePercent / 100),
      0
    );
    const aggregateTotalTokens = aggregateInputTokens + aggregateOutputTokens;

    // Server configuration
    const totalPopulation = personas.reduce((sum, p) => sum + p.population, 0);
    const finalServerCount = Math.ceil(snapshot.finalGpuCount / gpusPerServer);
    const usableBytesPerGpu = hbmBytesPerGpu ? hbmBytesPerGpu * 0.9 : undefined; // 10% headroom
    const averageUsedBytesPerGpu = snapshot.kvGiBP95 * (1024 ** 3) / snapshot.finalGpuCount;

    // Compute persona timings with per-session TPS
    const personaTimings: PersonaTiming[] = personas.map((p) => {
      const readSeconds = p.readSeconds ?? 0;
      const thinkSeconds = p.thinkSeconds ?? 0;
      const ttftMs = p.ttftMs ?? 0;
      const decodeTps = p.decodeTps ?? 1;
      const ttftSeconds = ttftMs / 1000;
      const streamSeconds = decodeTps > 0 ? p.outputTokensPerConversation / decodeTps : 0;
      const serviceTimeSec = ttftSeconds + streamSeconds;
      const cycleSec = readSeconds + thinkSeconds + serviceTimeSec;
      
      // Calculate per-session TPS (per the plan)
      const turnsKept = 1; // Default to 1 turn for now
      const promptTokensPerTurn = p.inputTokensPerConversation / turnsKept;
      const decodeTokensPerTurn = p.outputTokensPerConversation / turnsKept;
      const prefillSec = Math.max(ttftMs / 1000, 0.1); // Minimum 100ms for prefill
      const perSessionPromptTps = promptTokensPerTurn / prefillSec;
      const perSessionDecodeTps = decodeTps;
      
      const total = cycleSec > 0 ? cycleSec : 1;
      return {
        id: p.id,
        name: p.name,
        readSeconds,
        thinkSeconds,
        ttftMs,
        decodeTps,
        inputTokensPerConversation: p.inputTokensPerConversation,
        outputTokensPerConversation: p.outputTokensPerConversation,
        serviceTimeSec,
        cycleSec,
        turnsKept,
        promptTokensPerTurn,
        decodeTokensPerTurn,
        perSessionPromptTps,
        perSessionDecodeTps,
        dutyCycleBreakdown: {
          readPct: (readSeconds / total) * 100,
          thinkPct: (thinkSeconds / total) * 100,
          queryPct: (serviceTimeSec / total) * 100,
        },
      };
    });

    // Compute percentile curves (use available data only)
    const activePercentiles = [
      { percentile: 'P50', value: snapshot.p50Active },
      { percentile: 'P95', value: snapshot.p95Active },
    ];

    // Extract KV percentiles from kvSessionsByPercentile if available
    const kvPercentileKeys = Object.keys(snapshot.kvSessionsByPercentile ?? {}).map(Number).sort((a, b) => a - b);
    const kvPercentiles = kvPercentileKeys.length > 0
      ? kvPercentileKeys.map(p => ({
          percentile: `P${p}`,
          value: snapshot.kvSessionsByPercentile[p] ?? 0,
        }))
      : [
          { percentile: 'P50', value: snapshot.kvSessionsP50 },
          { percentile: 'P95', value: snapshot.kvSessionsP95 },
        ];

    // GPU Memory Breakdown
    const hbmGiB = hbmBytesPerGpu ? hbmBytesPerGpu / (1024 ** 3) : 0;
    const weightsPerGpu = weightsGiB ?? 0;
    const weightsUnshared = weightsGiBUnshared ?? weightsPerGpu;
    const runtimeOverhead = runtimeOverheadGiB ?? 0;
    const usableKvPerGpu = usableKvGiBPerGpu ?? Math.max(0, hbmGiB - weightsPerGpu - runtimeOverhead);
    const memoryRecaptured = weightsUnshared - weightsPerGpu;

    const gpuMemory = {
      hbmGiBPerGpu: hbmGiB,
      weightsGiBPerGpu: weightsPerGpu,
      weightsGiBUnshared: weightsUnshared,
      runtimeOverheadGiB: runtimeOverhead,
      usableKvGiBPerGpu: usableKvPerGpu,
      bridgeGroupSize,
      memoryRecapturedGiB: memoryRecaptured,
    };

    // Per-Persona KV Breakdown
    const personaKvBreakdowns = personas.map((p) => {
      const shareOfP95 = totalPopulation > 0 ? (p.population / totalPopulation) : 0;
      const sessionsP95 = snapshot.kvSessionsP95 * shareOfP95;
      
      // Tokens retained for this persona's session (match MC sizing assumptions)
      const tokensPerConversation = p.inputTokensPerConversation + p.outputTokensPerConversation;

      const kvBytesPerToken = snapshot.kvBytesPerToken;
      const kvGiBPerSession = (tokensPerConversation * kvBytesPerToken) / (1024 ** 3);
      const totalKvGiB = sessionsP95 * kvGiBPerSession;
      const avgKvGiBPerGpu = snapshot.finalGpuCount > 0 ? totalKvGiB / snapshot.finalGpuCount : 0;

      return {
        personaId: p.id,
        personaName: p.name,
        sessionsP95,
        tokensPerSession: tokensPerConversation,
        kvGiBPerSession,
        totalKvGiB,
        avgKvGiBPerGpu,
      };
    });

    // KV Sizing Comparison (theoretical vs bin-packed)
    // Theoretical = simple division (optimistic lower bound, assumes perfect packing)
    // Bin-packed = FFD result (realistic, accounts for fragmentation)
    // Overhead = additional GPUs needed due to fragmentation (always >= 0)
    const theoreticalKvGpus = usableKvPerGpu > 0 
      ? Math.ceil(snapshot.kvGiBP95 / usableKvPerGpu) 
      : snapshot.kvGpusP95;
    const binPackedKvGpus = snapshot.kvGpusP95;
    const overheadGpus = Math.max(0, binPackedKvGpus - theoreticalKvGpus);

    // Calculate GPUs needed if TP=1 (no bridging)
    const usableKvIfNoBridging = Math.max(0, hbmGiB - weightsUnshared - runtimeOverhead);
    const gpusIfNoBridging = usableKvIfNoBridging > 0 
      ? Math.ceil(snapshot.kvGiBP95 / usableKvIfNoBridging)
      : 999; // Would not fit

    const kvComparison = {
      theoreticalKvGpus,
      binPackedKvGpus,
      overheadGpus,
      gpusIfNoBridging,
    };
    
    // MC explanation flags
    const mcExplanation = {
      activeIsGeneratingOnly: true,
      kvResidentIncludesReadThink: true,
    };

    const narrativeData: SizingNarrativeData = {
      personas: personaSummaries,
      
      throughput: {
        inputTpsRequired: snapshot.requiredPromptTps,
        outputTpsRequired: snapshot.requiredDecodeTps,
        inputTpsPerGpu: snapshot.promptTpsPerGpu,
        outputTpsPerGpu: snapshot.decodeTpsPerGpu,
        gpusForInput: snapshot.promptGpus,
        gpusForOutput: snapshot.decodeGpus,
        gpusThroughput: Math.max(snapshot.promptGpus, snapshot.decodeGpus),
      },

      kv: {
        kvSessionsP95: snapshot.kvSessionsP95,
        kvGiBTotal: snapshot.kvGiBP95,
        gpusKv: snapshot.kvGpusP95,
        kvBytesPerToken: snapshot.kvBytesPerToken,
      },

      model: {
        modelName,
        modelParamsB: snapshot.precision === 8 ? Math.round(weightsGiB * 8 / 8) : Math.round(weightsGiB * 8 / 16),
        precision: `${snapshot.precision}-bit`,
        weightsGiBTotal: weightsGiB,
        gpusWeights: 1, // Minimum for now
      },

      finalPlan: {
        inputConcurrentUsers: totalPopulation,
        mcActiveP50: snapshot.p50Active,
        mcActiveP95: snapshot.p95Active,
        finalGpuCount: snapshot.finalGpuCount,
        finalServerCount,
        gpusPerServer,
        limitingConstraint: snapshot.finalBottleneck === 'kv' ? 'memory' : 
                           snapshot.finalBottleneck === 'prompt' ? 'throughput' :
                           snapshot.finalBottleneck === 'decode' ? 'throughput' : 'memory',
      },

      workload: {
        turns: conversationTurns,
        batch: batchSize,
        baseConcurrentUsers: totalPopulation,
        latencyMs,
        growthCagrPct: 0,
        totalPct: totalShare,
      },

      requests: {
        aggregateInputTokensPerConversation: aggregateInputTokens,
        aggregateOutputTokensPerConversation: aggregateOutputTokens,
        aggregateTotalTokensPerConversation: aggregateTotalTokens,
        tokensPerTurn: aggregateTotalTokens / conversationTurns,
      },

      serverConfig: {
        sku: serverSku,
        displayName: serverDisplayName,
        acceleratorsPerServer: gpusPerServer,
        throughputHeadroomPct: 0.1,
        memoryHeadroomPct: 0.1,
        promptTpsPerGpu: snapshot.promptTpsPerGpu,
        decodeTpsPerGpu: snapshot.decodeTpsPerGpu,
        rawPromptTpsPerGpu: snapshot.promptTpsPerGpu / 0.9,
        rawDecodeTpsPerGpu: snapshot.decodeTpsPerGpu / 0.9,
        throughputEstimated: false,
        promptTpsPerServer: snapshot.promptTpsPerGpu * gpusPerServer,
        decodeTpsPerServer: snapshot.decodeTpsPerGpu * gpusPerServer,
        hbmBytesPerGpu,
        usableBytesPerGpu,
        averageUsedBytesPerGpu,
        peakGpuUtilizationPct: usableBytesPerGpu ? averageUsedBytesPerGpu / usableBytesPerGpu : undefined,
        averageUsedBytesPerServer: averageUsedBytesPerGpu * gpusPerServer,
        usableBytesPerServer: usableBytesPerGpu ? usableBytesPerGpu * gpusPerServer : undefined,
      },

      fleetCapacity: {
        totalServers: finalServerCount,
        totalGpus: snapshot.finalGpuCount,
        usersPerServer: totalPopulation / finalServerCount,
        totalUsersCapacity: totalPopulation,
        memoryUtilizationPct: usableBytesPerGpu ? averageUsedBytesPerGpu / usableBytesPerGpu : undefined,
      },

      // Advanced analytics
      personaTimings,
      activePercentiles,
      kvPercentiles,
      bottleneckDetail: {
        promptGpus: snapshot.promptGpus,
        decodeGpus: snapshot.decodeGpus,
        kvGpus: snapshot.kvGpusP95,
        limiting: snapshot.finalBottleneck === 'prompt' ? 'prompt' : 
                  snapshot.finalBottleneck === 'decode' ? 'decode' :
                  snapshot.finalBottleneck === 'kv' ? 'kv' : 'none',
        utilization: {
          prompt: snapshot.finalGpuCount > 0 ? (snapshot.promptGpus / snapshot.finalGpuCount) * 100 : 0,
          decode: snapshot.finalGpuCount > 0 ? (snapshot.decodeGpus / snapshot.finalGpuCount) * 100 : 0,
          kv: snapshot.finalGpuCount > 0 ? (snapshot.kvGpusP95 / snapshot.finalGpuCount) * 100 : 0,
        },
      },

      // GPU Memory breakdown
      gpuMemory,
      personaKvBreakdowns,
      kvComparison,
      
      // MC explanation
      mcExplanation,
    };

    return narrativeData;
  }, [snapshot, modelName, serverSku, serverDisplayName, gpusPerServer, hbmBytesPerGpu, weightsGiB, weightsGiBUnshared, runtimeOverheadGiB, usableKvGiBPerGpu, bridgeGroupSize, conversationTurns, latencyMs, batchSize, personas]);
}
